import moment from 'moment';
import { MONETISATION_TYPE } from '../pages/utils';
import { IVaccineStatus, MHCWithVaccination } from '../store/types/medicalHistory';
import {
    Doctor,
    Prescription,
    DiagnosisEntityOrSymptomsEntity,
    VitalsEntity,
    MedicationsEntity,
    LabTestsEntityOrProceduresEntity,
    Followup,
    AdvicesEntity,
    ExaminationEntity,
    Notes,
    LabRefer,
    LabVitalEntity,
    Attachment,
    DentalExamination,
    DentalProcedure,
    OpVisionElement,
    OpSpecElement,
    OpIopElement,
    InjectionsEntity,
    Procedure,
    IpdAdmission,
} from '../store/types/prescription';
import { TemplateV2 } from '../Templar/RenderPdfPrescription';
import { getAgeInMonths } from '../store/utils';
import { PREIVEW_VISIT, PREIVEW_VISIT_V2, PUSH_VISIT, RX_PDF_HOST } from './constants';
import { CreateReferalData } from './createReferal';
import fetchWithRefresh from './fetchWithRefresh';
import { GenieItem } from '../store/db';
import { IMilestoneHistory } from '../components/common/types/milestones';
import { CareCanvas } from '../store/types/CareCanvas';
import { getQueueConfig, isOnNewQueueView } from '../store/localstorage';
import { updateAppointment } from '../firestore/appointments';
import { serverTimestamp, Timestamp } from 'firebase/firestore';
import {
    MESSAGE_TYPE,
    sendAppointmentCommunication,
} from '../firestore/appointments/communications';
import { getGById, getItemFromMisc, getMe, MISC_KEYS } from '../store/idbHandler';
import { GenieSettings } from './pushGenie';
import { getMyDbId } from './mactiveid';
import { BizSettings } from './fetchMyGenie';
import { getDefiniteMActiveId } from '../store/localstorage';
import { ResData } from '../components/prescriptionPad/rightPanel/CreatelocalPdf';
import { Appointment } from './fetchAppointmentsV2';
import { DB_TABLES } from '../store/dbTables';
import { track } from '../tracking';
import {
    makeFirestoreTTL_For6months,
    recordAppointmentActivityInFS,
} from '../utils/appointmentActivityRecorderInFS';
import { Actor, AppointmentActivityType, SourcePlatform } from '../Bridge';

export const ALREADY_SAVED = 'ALREADY_SAVED';
export const LATEST_DRAFT_NOT_APPLIED = 'LATEST_DRAFT_NOT_APPLIED';

export interface PatientPersonalProfile {
    name: string;
    phone?: {
        c?: string;
        n?: string;
    };
    age?: { dob?: string; ageInMonths?: number };
    gender: string;
}

export interface PatientProfile {
    personal: PatientPersonalProfile;
}

export interface PatientWithLocalId {
    id?: string;
    localId: string;
    profile: PatientProfile;
}

export type PrintConfigItemV2 = {
    entity: boolean;
    [k: string]: boolean;
};

export type DyFormPrintConfigItem = { [id: string]: boolean };
export type PrintConfigItem = PrintConfigItemV2 | boolean | DyFormPrintConfigItem;

export type PrintConfig = {
    [k: string]: PrintConfigItem;
    dyform: { [id: string]: boolean };
};

export type Template = {
    templateId?: string;
    type?: string;
};

export type Translate = {
    flag: boolean;
    lang: '' | 'en' | 'hi' | string;
    advices?: boolean;
    medications?: boolean;
    injections?: boolean;
};

export interface PushDataRequest {
    data: {
        id: string;
        date: Date;
        dateEnd: Date;
        selectedClinic: string;
        visitId: string;
        doctor: Doctor;
        patient: PatientWithLocalId;
        tool: Tool;
        padVersion: 'v1' | 'v2';
    };
    template: Template;
    printConfig: PrintConfig;
    translate: Translate;
    template_v2?: TemplateV2;
    send_rx_message?: boolean;
    monetisation_type?: MONETISATION_TYPE | '';
    template_print_v2?: TemplateV2;
}

export interface PadFDFormElement extends GenieItem {
    id: string;
    name?: string;
    value?: string;
    icd10_name?: string;
    icd10_code?: string;
}

export type PadDForm = {
    id: string;
    name: string;
    elements?: PadFDFormElement[];
};

export interface Tool {
    dyform?: PadDForm[];
    language?: string;
    diagnosis?: DiagnosisEntityOrSymptomsEntity[];
    symptoms?: DiagnosisEntityOrSymptomsEntity[];
    medications?: MedicationsEntity[];
    injections?: InjectionsEntity[];
    labTests?: LabTestsEntityOrProceduresEntity[];
    followup?: Followup;
    advices?: AdvicesEntity[];
    prescriptionNotes?: Notes;
    privateNotes?: Notes;
    medicalHistory?: {
        patientHistory?: MHCWithVaccination;
        examinations?: ExaminationEntity[];
        vitals?: VitalsEntity[];
    };
    meta?: {
        templateId?: string;
    };
    refer?: CreateReferalData;
    labRefer?: LabRefer;
    labVitals?: LabVitalEntity[];
    attachment?: Attachment;
    dentalExaminations?: DentalExamination[];
    dentalProcedures?: DentalProcedure[];
    administeredVaccines?: IVaccineStatus[];
    milestones?: IMilestoneHistory[];
    allMilestones?: IMilestoneHistory[];
    vaccinationHistory?: IVaccineStatus[];
    opVision?: OpVisionElement[];
    opIop?: OpIopElement[];
    opCurrentSpec?: OpSpecElement[];
    opSubjectiveRefraction?: OpSpecElement[];
    opAutoRefraction?: OpSpecElement[];
    opFinalPrescription?: OpSpecElement[];
    procedures?: Procedure[];
    ipdAdmission?: IpdAdmission;
    careCanvas?: CareCanvas[];
    allVaccines?: IVaccineStatus[];
}

export enum COMMUNICATION {
    LATER = 'later',
    OFF = 'off',
    SENT = 'sent',
    ERROR = 'error',
    NOT_APPLICABLE = 'na',
}

export interface Response {
    data?: ResData;
    status?: string;
    communication?: COMMUNICATION;
    saved: boolean;
    notifSend: boolean;
    wpString?: string;
    url?: string;
    pid?: string;
    visitName?: string;
    canPay?: boolean;
    attachmentSend?: boolean;
    consentGiven: boolean;
}

export default async (
    visitData: Prescription,
    startedAt: Date,
    endedAt: Date,
    context: 'send' | 'print',
    printConfig: PrintConfig,
    translate: Translate,
    templateId?: string,
    template_v2?: TemplateV2,
    send_rx_message?: boolean,
    force: boolean = false,
    monetisation_type?: MONETISATION_TYPE | '',
    ignoredraft: boolean = false,
    draftid: string = '',
    isFromCreatelocalPdf: boolean = false,
    template_print_v2?: TemplateV2,
): Promise<Response> => {
    const { id, visitId, doctor, clinic, patient } = visitData;

    const ageInMonths =
        patient.profile.personal.age && getAgeInMonths(patient.profile.personal.age);

    const pushPatient: PatientWithLocalId = {
        ...patient,
        profile: {
            ...patient.profile,
            personal: {
                ...patient.profile.personal,
                age: patient.profile.personal.age?.dob
                    ? { dob: moment(patient.profile.personal.age?.dob).format('YYYY-MM-DD') }
                    : ageInMonths
                    ? { ageInMonths: ageInMonths }
                    : undefined,
            },
        },
    };

    const reqBody: PushDataRequest = {
        data: {
            id,
            date: startedAt,
            dateEnd: endedAt,
            selectedClinic: clinic._id,
            visitId,
            doctor,
            patient: pushPatient,
            tool: createBETool(visitData),
            padVersion: 'v2',
        },
        template: {
            templateId: templateId,
            type: context,
        },
        template_v2: template_v2,
        printConfig,
        translate,
        send_rx_message: send_rx_message,
        monetisation_type: monetisation_type,
        template_print_v2: template_print_v2,
    };

    const bizSettings = await getItemFromMisc<BizSettings>(
        getDefiniteMActiveId() || '',
        MISC_KEYS.bizSettings,
    );

    return fetchWithRefresh(
        context === 'send'
            ? PUSH_VISIT(force, ignoredraft, draftid)
            : isFromCreatelocalPdf && !bizSettings.summaryPdfLocalDisabled
            ? PREIVEW_VISIT_V2(force)
            : PREIVEW_VISIT(force),
        {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(reqBody),
        },
    ).then(async (r) => {
        if (r.status === 406) {
            throw new Error(ALREADY_SAVED);
        } else if (r.status === 409) {
            throw new Error(LATEST_DRAFT_NOT_APPLIED);
        } else if (r.status === 200) {
            if (context === 'send') {
                const apt = await getGById<Appointment | undefined>(
                    getDefiniteMActiveId(),
                    DB_TABLES.appointments,
                    reqBody.data.visitId,
                );

                if (!apt) {
                    track('WARNING_APT_NOT_FOUND', {
                        aid: reqBody.data.visitId,
                        on: 'pushVisit',
                        what: 'apt not found',
                    });

                    if (!window.bookSlot) {
                        track('ERROR', {
                            aid: reqBody.data.visitId,
                            on: 'pushVisit',
                            what: 'book slot not found apt not found',
                        });
                        return Promise.reject('Appointment bookSlot not found');
                    }

                    await window.bookSlot({
                        aid: reqBody.data.visitId,
                        channel: 'Walkin',
                        mode: 'in-clinic',
                        clinicId: reqBody.data.selectedClinic,
                        doctorId: reqBody.data.doctor._id,
                        patient: {
                            oid: (patient?.localId || patient?.id) as string,
                            allowMultiBooking: getQueueConfig()?.apt_config?.allow_pt_multi_apt,
                        },
                        startTime: new Date().toISOString(),
                        endTime: new Date().toISOString(),
                        action: 'CK',
                    });
                }

                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                if (window.firestore) {
                    await updateAppointment({
                        aid: reqBody.data.visitId,
                        data: {
                            date: Timestamp.fromDate(
                                new Date(reqBody.data.dateEnd || reqBody.data.date),
                            ),
                            full_date: Timestamp.fromDate(
                                new Date(reqBody.data.dateEnd || reqBody.data.date),
                            ),
                            patient_oid: reqBody.data.patient.id,
                            status: 'CM',
                            // patient_uuid: reqBody.data.patient.profile?.uuid || appointment.patient_uuid || null,
                            visit_end: Timestamp.fromDate(
                                new Date(reqBody.data.dateEnd || reqBody.data.date),
                            ),
                            prescription_id: reqBody.data.id,
                            prescription_url: `${RX_PDF_HOST}/${reqBody.data.id}.pdf`,
                            print_prescription_url: `${RX_PDF_HOST}/${reqBody.data.id}-review-print.pdf`,
                            follow_up: reqBody.data.tool.followup?.date
                                ? Timestamp.fromDate(new Date(reqBody.data.tool.followup?.date))
                                : null,
                        },
                    });

                    const actorProfile = getMe();
                    const appointmentActivityDataMap = {
                        event: AppointmentActivityType.VISIT_ENDED,
                        actor_id: actorProfile!.user.oid!,
                        client_id: SourcePlatform.WEB,
                        actor_role: actorProfile!.user['is-d-s'] ? Actor.STAFF : Actor.DOCTOR,
                        created_at: serverTimestamp(),
                        expires_at: makeFirestoreTTL_For6months(),
                        actor_name: actorProfile!.user!.fn,
                    };

                    await recordAppointmentActivityInFS(
                        appointmentActivityDataMap,
                        reqBody.data.visitId,
                    );

                    await sendAppointmentCommunication({
                        data: { msg_type: MESSAGE_TYPE.END_VISIT },
                        aid: reqBody.data.visitId,
                    });
                } else {
                    console.error('window.firestore not found');
                }
            }
        }
        return r.json();
    });
};

export const createBETool = ({
    symptoms,
    diagnosis,
    medications,
    injections,
    labTests,
    followup,
    advices,
    meta,
    vitals,
    examinations,
    refer,
    prescriptionNotes,
    privateNotes,
    patientHistory,
    dyform,
    labRefer,
    labVitals,
    attachment,
    dentalExaminations,
    dentalProcedures,
    administeredVaccines,
    milestones,
    opVision,
    opIop,
    opCurrentSpec,
    opSubjectiveRefraction,
    opAutoRefraction,
    opFinalPrescription,
    procedures,
    ipdAdmission,
    careCanvas,
}: Prescription): Tool => {
    return {
        language: 'EN',
        symptoms,
        diagnosis,
        medications,
        injections,
        labTests,
        followup,
        advices,
        meta,
        prescriptionNotes: prescriptionNotes,
        privateNotes: privateNotes,
        medicalHistory: {
            patientHistory: patientHistory,
            vitals,
            examinations,
        },
        refer,
        dyform,
        labRefer,
        labVitals,
        attachment,
        dentalExaminations,
        dentalProcedures,
        administeredVaccines,
        milestones,
        opVision,
        opIop,
        opCurrentSpec,
        opSubjectiveRefraction,
        opAutoRefraction,
        opFinalPrescription,
        procedures,
        ipdAdmission,
        careCanvas,
    };
};
